import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Alert, Box, Button, Skeleton, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { api } from "../utils/helpers";
import { stageLabel } from "../utils/stages";
import CicdPlantTargetCatalog, { DEFAULT_ID, baseStageOf, catalogColumns, colName, idOf, normalizeCatalog, plantLabel, searchSx } from "./CicdPlantTargetCatalog";
import { MapNode } from "./PlantInstanceMappingNode";
import "./PlantInstanceMapping.css";

// Werks-Instanz-Mapping (Welle 5, Vertrag V5.4): links die Zielinstanzen je Mehrzielstufe, rechts die Werke des Space.
// Eine Kante (Werk, Instanz) steht für targetId(Instanz) in entries[kennung][stage]. Ohne eigene Kante gilt in einer
// Stufe automatisch das Standardziel, diese Kante ist gestrichelt. Kanten entstehen durch Ziehen in beide Richtungen
// (Pointer-Muster aus StageDrag.jsx; auf Touch nur am Griff) oder per Auswahl zweier Knoten mit Klick bzw. Enter.
// Klick, Enter, Leertaste oder Entf auf einer Kante entfernt sie. Die Tabelle (CicdPlantTargetCatalog) bearbeitet
// denselben Entwurf und ist der Tastatur- und Barrierefreiheitsweg.
//
// Leistung (bis 200 Werke und 50 Instanzen): Knoten sind memoisiert (PlantInstanceMappingNode), Bedienung läuft
// delegiert über die Zeichenfläche, Gummiband, Ablageziel und Hervorhebung werden über Refs direkt am DOM geführt
// (requestAnimationFrame) und lösen kein Rendern aus.
const DRAG_THRESHOLD_PX = 6;
const AUTO_SCROLL_EDGE_PX = 40;
const AUTO_SCROLL_MAX_STEP = 18;
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
const plantKeyOf = kennung => `plant:${kennung}`;
const instanceKeyOf = (stage, id) => `${stage}:${id}`;
const attr = value => String(value).replace(/["\\]/g, "\\$&");

/** Kantenpfad wie in PipelineTopology.jsx: kubische Bezierkurve mit waagerechten Tangenten. */
const wirePath = (x1, y1, x2, y2) => {
  const c = (x2 - x1) * 0.45;
  return `M${x1},${y1} C${x1 + c},${y1} ${x2 - c},${y2} ${x2},${y2}`;
};

/** Ankerpunkte eines Knotens relativ zur Zeichenfläche; offsetTop/offsetLeft ignorieren Transformationen und Scrollen. */
function measureNode(el, canvas) {
  let left = 0, top = 0, node = el;
  while (node && node !== canvas) { left += node.offsetLeft || 0; top += node.offsetTop || 0; node = node.offsetParent; }
  return { left, right: left + (el.offsetWidth || 0), y: top + (el.offsetHeight || 0) / 2 };
}

/** Misst alle Knoten neu, wenn sich das Layout ändert (ResizeObserver, Fenstergröße). */
function useNodeGeometry(canvasRef, layoutKey) {
  const [geometry, setGeometry] = useState({ width: 0, height: 0, nodes: {} });
  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    let frame = null;
    const measure = () => {
      frame = null;
      const nodes = {};
      canvas.querySelectorAll("[data-card-id]").forEach(el => { nodes[el.dataset.cardId] = measureNode(el, canvas); });
      const next = { width: canvas.offsetWidth || 0, height: canvas.offsetHeight || 0, nodes };
      setGeometry(prev => same(prev, next) ? prev : next);
    };
    const schedule = () => { if (frame === null) frame = requestAnimationFrame(measure); };
    measure();
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(schedule) : null;
    observer?.observe(canvas);
    canvas.querySelectorAll("[data-map-column]").forEach(el => observer?.observe(el));
    window.addEventListener("resize", schedule);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", schedule);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [canvasRef, layoutKey]);
  return geometry;
}

function usePrefersReducedMotion() {
  const query = "(prefers-reduced-motion: reduce)";
  const read = () => { try { return !!window.matchMedia?.(query)?.matches; } catch { return false; } };
  const [reduced, setReduced] = useState(read);
  useEffect(() => {
    const media = window.matchMedia?.(query);
    const onChange = () => setReduced(!!media?.matches);
    media?.addEventListener?.("change", onChange);
    return () => media?.removeEventListener?.("change", onChange);
  }, []);
  return reduced;
}

// --- Gestaltung: alle sx-Objekte sind Konstanten, Farben nur über var(--iio-*) (Muster SpaceWerkeDialog) -------------
const alertSx = tone => ({
  fontFamily: "inherit", fontSize: 13, lineHeight: 1.5, borderRadius: "6px", alignItems: "flex-start",
  color: "var(--iio-text-1)",
  background: `color-mix(in srgb, var(--iio-${tone}) 6%, var(--iio-bg-1))`,
  border: `1px solid color-mix(in srgb, var(--iio-${tone}) 35%, var(--iio-border))`,
  "& .MuiAlert-icon": { color: `var(--iio-${tone})` },
  "& .MuiAlert-message": { minWidth: 0, flex: 1, overflowWrap: "anywhere" },
  "& .MuiAlert-action": { alignItems: "center", pt: 0 },
});
const ALERT = { info: alertSx("info"), warning: alertSx("warning"), critical: alertSx("critical"), success: alertSx("success") };
const focusVisible = { outline: "2px solid var(--iio-brand-fg)", outlineOffset: 2 };
const buttonSx = { textTransform: "none", fontFamily: "inherit", borderRadius: "6px", minHeight: 36, boxShadow: "none", "&.Mui-focusVisible": focusVisible };
const saveSx = { ...buttonSx, background: "var(--iio-signal)", color: "var(--iio-hero-text)",
  "&:hover": { background: "var(--iio-signal-2)", boxShadow: "none" }, "&:active": { boxShadow: "none" }, "&.Mui-focusVisible": { ...focusVisible, boxShadow: "none" },
  "&.Mui-disabled": { background: "var(--iio-bg-3)", color: "var(--iio-text-3)" } };
const discardSx = { ...buttonSx, color: "var(--iio-text-1)", borderColor: "var(--iio-border)",
  "&:hover": { color: "var(--iio-text-1)", borderColor: "var(--iio-text-3)", background: "var(--iio-bg-3)" },
  "&.Mui-disabled": { color: "var(--iio-text-3)", borderColor: "var(--iio-border-subtle)" } };
const actionSx = { ...buttonSx, minHeight: 28, color: "inherit" };
const filterSx = { ...searchSx, width: "auto", minWidth: 160,
  "& .MuiInputLabel-root": { fontFamily: "inherit", color: "var(--iio-text-3)" },
  "& .MuiInputLabel-root.Mui-focused": { color: "var(--iio-brand-fg)" } };
const graphSearchSx = { ...searchSx, width: 240 };
const toggleSx = {
  "& .MuiToggleButton-root": { textTransform: "none", fontFamily: "inherit", fontSize: 13, px: 2, borderRadius: "6px", color: "var(--iio-text-2)", borderColor: "var(--iio-border)" },
  "& .MuiToggleButton-root:hover": { color: "var(--iio-text-1)", background: "var(--iio-bg-3)" },
  "& .MuiToggleButton-root.Mui-selected, & .MuiToggleButton-root.Mui-selected:hover": {
    color: "var(--iio-brand-ink)", background: "color-mix(in srgb, var(--iio-brand-ink) 10%, var(--iio-bg-2))", fontWeight: 600,
    '[data-theme="dark"] &': { color: "var(--iio-info)" } },
  "& .MuiToggleButton-root.Mui-focusVisible": { outline: "2px solid var(--iio-brand-fg)", outlineOffset: -2 },
};
const rootSx = { display: "flex", flexDirection: "column", gap: 1.5, minWidth: 0, fontVariantNumeric: "tabular-nums" };
const introSx = { fontFamily: "inherit", fontSize: 13, lineHeight: 1.45, color: "var(--iio-text-2)", maxWidth: "90ch" };
const toolsSx = { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5 };
const legendSx = { display: "flex", flexWrap: "wrap", gap: 2.5, fontSize: 12, color: "var(--iio-text-2)" };
const legendItemSx = { display: "inline-flex", alignItems: "center", gap: 1 };
const scrollSx = { overflow: "auto", maxHeight: "min(62vh, 640px)", border: "1px solid var(--iio-border)", borderRadius: "4px", background: "var(--iio-bg-1)", overscrollBehavior: "contain" };
const canvasSx = { position: "relative", display: "grid", gridTemplateColumns: "minmax(200px, 1fr) minmax(88px, 160px) minmax(200px, 1fr)",
  alignItems: "start", minWidth: 520, px: 2, pt: 1.5, pb: 2 };
const columnSx = { minWidth: 0 };
const colTitleSx = { m: 0, fontFamily: "inherit", fontSize: 12, fontWeight: 600, color: "var(--iio-text-3)", textTransform: "uppercase", letterSpacing: ".06em" };
const stageHeadSx = { m: 0, pb: .5, borderBottom: "1px solid var(--iio-border-subtle)", fontFamily: "inherit", fontSize: 13, fontWeight: 600, color: "var(--iio-text-1)" };
const listSx = { listStyle: "none", m: 0, p: 0 };
const mutedSx = { fontFamily: "inherit", fontSize: 12, color: "var(--iio-text-2)" };
const statusSx = { minHeight: "1.45em", fontFamily: "inherit", fontSize: 13, color: "var(--iio-text-2)" };
const footSx = { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5 };
const skeletonSx = { bgcolor: "var(--iio-bg-3)", borderRadius: "6px" };

// Ladefehler als Code statt Text: übersetzt wird beim Rendern, damit ein Sprachwechsel nichts neu laden muss.
const KNOWN_LOAD = { CICD_SETUP_REQUIRED: "plantMapping.setupRequired", CICD_MANAGE_REQUIRED: "plantMapping.manageRequired", STAGE_MULTI_TARGET_DISABLED: "plantTargets.disabled" };
const loadInfo = e => {
  const key = KNOWN_LOAD[e?.code] || (e?.status === 403 ? "plantMapping.manageRequired" : null);
  return key ? { kind: "note", key } : { kind: "crit", grund: e?.message || null };
};
const loadReason = (info, t) => info.key ? t(info.key) : info.grund || t("plantTargets.grundUnbekannt");

// Kanten zu nicht eingerichteten Zielen (configured false) wirken nicht, bis die Instanz eingerichtet ist (be-pending M3,
// Feld ineffective der Route). Dieselbe Regel für den Entwurf, damit die Kennzeichnung schon vor dem Speichern stimmt.
export function ineffectiveEdges(entries, columns) {
  const pending = new Set(columns.flatMap(c => c.rows.filter(r => r.configured === false).map(r => instanceKeyOf(c.key, idOf(r)))));
  const out = [];
  if (!pending.size) return out;
  for (const [kennung, byStage] of Object.entries(entries || {})) for (const [stage, ids] of Object.entries(byStage || {})) {
    for (const targetId of ids || []) if (pending.has(instanceKeyOf(stage, targetId))) out.push({ kennung, stage, targetId });
  }
  return out;
}

// canEdit: ohne Angabe entscheidet die Route. GET /api/cicd/plant-targets verlangt dasselbe Recht wie PUT (CI/CD
// Verwaltungsrecht am Projekt); wer die Antwort bekommt, darf speichern. false erzwingt nur Lesen, data.editable false ebenso.
// footer (optional): { status, actions } sind DOM-Elemente im Fuß des umgebenden Dialogs (SpaceWerkeDialog, DialogActions).
// Dann stehen Zähler und Ansage (aria-live) in status, Speichern und Verwerfen in actions, also immer sichtbar neben „Schließen“
// statt am Ende der scrollenden Zeichenfläche. Ohne footer bleibt der Fuß in der Komponente (Einzelbetrieb, Tests).
export default function PlantInstanceMapping({ spaceId, canEdit = null, onDirtyChange = null, footer = null }) {
  const { t } = useTranslation("cicdsetup");
  const reducedMotion = usePrefersReducedMotion();
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [loadError, setLoadError] = useState(null); // loadInfo(e)
  const [message, setMessage] = useState(null); // { tone, render: t => string, conflict? }
  const [announcement, setAnnouncement] = useState("");
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState("graph");
  const [query, setQuery] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [picked, setPicked] = useState(null); // Knoten für das Verbinden per Klick oder Tastatur
  const mounted = useRef(false);
  const canvasRef = useRef(null);
  const scrollRef = useRef(null);
  const svgRef = useRef(null);
  const rubberRef = useRef(null);
  const sessionRef = useRef(null);
  const suppressClickRef = useRef(false);
  const hotRef = useRef({ key: null, paths: [] });
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  const load = useCallback(async ({ keep = false } = {}) => {
    if (!keep) setLoadError(null);
    try {
      const res = await api(`/api/cicd/plant-targets?spaceId=${encodeURIComponent(spaceId)}`);
      if (!mounted.current) return { ok: true };
      setData(res || {}); setDraft(res?.entries || {});
      return { ok: true };
    } catch (e) {
      const info = loadInfo(e);
      if (mounted.current && !keep) { setData(null); setLoadError(info); }
      return { ok: false, info };
    }
  }, [spaceId]);
  useEffect(() => { setData(null); setMessage(null); setPicked(null); load(); }, [load]);

  const columns = useMemo(() => (data ? catalogColumns(data) : []), [data]);
  const baseStage = useMemo(() => baseStageOf(data, columns), [data, columns]);
  const plants = useMemo(() => (Array.isArray(data?.plants) ? data.plants : []), [data]);
  const plantKeys = useMemo(() => plants.map(p => p.kennung), [plants]);
  const original = useMemo(() => normalizeCatalog(data?.entries, columns, plantKeys), [data, columns, plantKeys]);
  const current = useMemo(() => normalizeCatalog(draft, columns, plantKeys), [draft, columns, plantKeys]);
  const changed = useMemo(() => Object.keys({ ...original, ...current }).filter(k => !same(original[k], current[k])), [original, current]);
  const staleCount = (data?.stale || []).length;
  const dirty = changed.length > 0;
  const mode = data?.mode || (columns.length ? "active" : "single");
  const allowed = canEdit ?? (data?.editable !== false);
  const editable = allowed && mode !== "single";
  const interactive = editable && !busy;
  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);

  const needle = query.trim().toLowerCase();
  const visiblePlants = useMemo(() => plants.filter(p => !needle || `${p.name || ""} ${p.kennung}`.toLowerCase().includes(needle)), [plants, needle]);
  const visibleColumns = useMemo(() => columns.filter(c => stageFilter === "all" || c.key === stageFilter), [columns, stageFilter]);
  const plantByKey = useMemo(() => new Map(plants.map(p => [p.kennung, p])), [plants]);

  // Ziele, die noch nicht eingerichtet sind (Schlüssel stage:targetId).
  const unconfigured = useMemo(() => new Set(columns.flatMap(c => c.rows.filter(r => r.configured === false).map(r => instanceKeyOf(c.key, idOf(r))))), [columns]);
  // Ohne ungespeicherte Änderung gilt die Antwort der Route, sonst dieselbe Regel auf den Entwurf.
  // Angezeigt in der Reihenfolge der Werke und Stufen, unabhängig von der Reihenfolge der Einträge.
  const ineffective = useMemo(() => {
    const list = !dirty && Array.isArray(data?.ineffective) ? data.ineffective : ineffectiveEdges(current, columns);
    const plantRank = new Map(plantKeys.map((k, i) => [k, i])), stageRank = new Map(columns.map((c, i) => [c.key, i]));
    const rank = (map, key) => (map.has(key) ? map.get(key) : Number.MAX_SAFE_INTEGER);
    return [...list].sort((a, b) => rank(plantRank, a.kennung) - rank(plantRank, b.kennung) || rank(stageRank, a.stage) - rank(stageRank, b.stage)
      || String(a.targetId).localeCompare(String(b.targetId)));
  }, [dirty, data, current, columns, plantKeys]);
  const sharingGaps = Array.isArray(data?.sharingGaps) ? data.sharingGaps : [];
  // Kanten der sichtbaren Werke und Stufen: eigene (durchgezogen) und automatische Standardkanten (gestrichelt).
  const edges = useMemo(() => {
    const out = [];
    for (const plant of visiblePlants) {
      for (const stage of visibleColumns) {
        const own = current[plant.kennung]?.[stage.key];
        if (own) own.forEach(id => out.push({ kennung: plant.kennung, stage: stage.key, id, kind: "own", locked: stage.key === baseStage && id === DEFAULT_ID,
          ineffective: unconfigured.has(instanceKeyOf(stage.key, id)) }));
        else out.push({ kennung: plant.kennung, stage: stage.key, id: DEFAULT_ID, kind: "default", locked: true });
      }
    }
    return out;
  }, [visiblePlants, visibleColumns, current, baseStage, unconfigured]);
  // Werkszahl je Instanz über alle Werke, unabhängig von Suche und Stufenfilter.
  const counts = useMemo(() => {
    const acc = {};
    for (const plant of plants) for (const stage of columns) {
      for (const id of current[plant.kennung]?.[stage.key] || [DEFAULT_ID]) acc[instanceKeyOf(stage.key, id)] = (acc[instanceKeyOf(stage.key, id)] || 0) + 1;
    }
    return acc;
  }, [plants, columns, current]);

  const layoutKey = `${view}|${mode}|${visibleColumns.map(c => c.key + c.rows.length).join(",")}|${visiblePlants.map(p => p.kennung).join(",")}|${editable}|${busy}`;
  const geometry = useNodeGeometry(canvasRef, data && view === "graph" && mode !== "single" ? layoutKey : null);

  const names = useCallback((kennung, stage, id) => {
    const plant = plantByKey.get(kennung);
    const row = columns.find(c => c.key === stage)?.rows.find(r => idOf(r) === id) || null;
    return { werk: plant ? plantLabel(plant) : kennung, ziel: row ? colName(row) : id, stage: stageLabel(stage) };
  }, [plantByKey, columns]);

  // --- Hervorhebung direkt am SVG: Kanten des Knotens unter Zeiger oder Fokus, ohne Neurendern -----------------
  const highlight = useCallback(key => {
    const svg = svgRef.current;
    const prev = hotRef.current;
    if (!svg) { hotRef.current = { key: null, paths: [] }; return; }
    prev.paths.forEach(p => p.classList.remove("is-hot"));
    const paths = key ? [...svg.querySelectorAll(`[data-a="${attr(key)}"],[data-b="${attr(key)}"]`)] : [];
    paths.forEach(p => p.classList.add("is-hot"));
    svg.classList.toggle("has-hot", !!key);
    hotRef.current = { key, paths };
  }, []);
  useLayoutEffect(() => { if (hotRef.current.key) highlight(hotRef.current.key); }, [edges, highlight]);
  useEffect(() => { highlight(picked?.key || null); }, [picked, highlight]);

  // --- Entwurf ändern (gleiche Regel wie die Tabelle) -------------------------------------------------------------
  const writeStage = (kennung, stage, list) => {
    const perStage = { ...(current[kennung] || {}) };
    if (list.length) perStage[stage] = list; else delete perStage[stage];
    const next = { ...current };
    if (Object.keys(perStage).length) next[kennung] = perStage; else delete next[kennung];
    setDraft(next);
    setMessage(null);
  };
  const connect = (kennung, stage, id) => {
    const own = current[kennung]?.[stage];
    const label = names(kennung, stage, id);
    if (!own && id === DEFAULT_ID) { setAnnouncement(t("plantMapping.alreadyDefault", label)); return; }
    if (own?.includes(id)) { setAnnouncement(t("plantMapping.alreadyEdge", label)); return; }
    let list = own ? [...own, id] : [id];
    if (stage === baseStage && !list.includes(DEFAULT_ID)) list = [DEFAULT_ID, ...list];
    writeStage(kennung, stage, list);
    setAnnouncement(t("plantMapping.added", label));
  };
  const disconnect = (kennung, stage, id, { refocus = false } = {}) => {
    const own = current[kennung]?.[stage];
    if (!own?.includes(id)) return;
    if (stage === baseStage && id === DEFAULT_ID) { setAnnouncement(t("plantTargets.baseLocked")); return; }
    writeStage(kennung, stage, own.filter(x => x !== id));
    setAnnouncement(t("plantMapping.removed", names(kennung, stage, id)));
    // Die Kante verschwindet mit dem Rendern; der Fokus geht an das Werk, damit die Tastatur nicht ins Leere läuft.
    if (refocus) canvasRef.current?.querySelector(`[data-map-node="plant"][data-kennung="${attr(kennung)}"]`)?.focus();
  };
  const link = (a, b) => {
    if (!a || !b || a.kind === b.kind) return false;
    const plant = a.kind === "plant" ? a : b, instance = a.kind === "plant" ? b : a;
    connect(plant.kennung, instance.stage, instance.id);
    return true;
  };
  const nodeOf = el => {
    const target = el?.closest?.("[data-map-node]");
    if (!target || !canvasRef.current?.contains(target)) return null;
    return target.dataset.mapNode === "plant"
      ? { kind: "plant", kennung: target.dataset.kennung, key: target.dataset.cardId, el: target }
      : { kind: "instance", stage: target.dataset.stage, id: target.dataset.targetId, key: target.dataset.cardId, el: target };
  };
  const nodeAtPoint = (x, y) => nodeOf(typeof document.elementFromPoint === "function" ? document.elementFromPoint(x, y) : null);

  // Stabile Ereignisfunktionen lesen den letzten Stand über eine Ref.
  const latest = useRef(null);
  latest.current = { interactive, picked, link, disconnect, names, t };

  // --- Ziehen: Pointer-Muster aus StageDrag.jsx, Gummiband und Ablageziel per requestAnimationFrame ---------------
  const setDrop = (session, node) => {
    if (session.dropEl === (node?.el || null)) return;
    session.dropEl?.removeAttribute("data-drop");
    session.dropEl = node?.el || null;
    session.dropEl?.setAttribute("data-drop", "true");
  };
  const stopDrag = useCallback(() => {
    const session = sessionRef.current;
    if (!session) return;
    sessionRef.current = null;
    document.removeEventListener("pointermove", session.onMove);
    document.removeEventListener("pointerup", session.onUp);
    document.removeEventListener("pointercancel", session.onCancel);
    document.removeEventListener("keydown", session.onKey, true);
    scrollRef.current?.removeEventListener("scroll", session.onScroll);
    if (session.frame !== null) cancelAnimationFrame(session.frame);
    if (session.active) {
      document.body.style.cursor = session.prevCursor ?? "";
      canvasRef.current?.classList.remove("is-dragging");
      session.dropEl?.removeAttribute("data-drop");
      const rubber = rubberRef.current;
      if (rubber) { rubber.removeAttribute("data-rubber"); rubber.style.display = "none"; rubber.classList.remove("is-valid"); }
      highlight(latest.current.picked?.key || null);
    }
  }, [highlight]);
  useEffect(() => stopDrag, [stopDrag]);

  const dragFrame = session => {
    session.frame = null;
    if (sessionRef.current !== session || !session.active) return;
    const canvas = canvasRef.current, box = scrollRef.current, rubber = rubberRef.current;
    // Rand-Auto-Scroll: Zeiger näher als 40 px am Rand der Scrollfläche.
    let scrolled = false;
    if (box?.getBoundingClientRect) {
      const r = box.getBoundingClientRect();
      const step = d => Math.ceil(AUTO_SCROLL_MAX_STEP * (1 - Math.max(0, d) / AUTO_SCROLL_EDGE_PX));
      const before = [box.scrollTop, box.scrollLeft];
      if (r.bottom - session.y < AUTO_SCROLL_EDGE_PX) box.scrollTop += step(r.bottom - session.y);
      else if (session.y - r.top < AUTO_SCROLL_EDGE_PX) box.scrollTop -= step(session.y - r.top);
      if (r.right - session.x < AUTO_SCROLL_EDGE_PX) box.scrollLeft += step(r.right - session.x);
      else if (session.x - r.left < AUTO_SCROLL_EDGE_PX) box.scrollLeft -= step(session.x - r.left);
      scrolled = before[0] !== box.scrollTop || before[1] !== box.scrollLeft;
    }
    const target = nodeAtPoint(session.x, session.y);
    const valid = target && target.kind !== session.from.kind ? target : null;
    setDrop(session, valid);
    if (rubber && canvas) {
      const rect = canvas.getBoundingClientRect?.() || { left: 0, top: 0 };
      const px = session.x - rect.left, py = session.y - rect.top;
      const a = measureNode(session.from.el, canvas);
      const d = session.from.kind === "plant" ? wirePath(px, py, a.left, a.y) : wirePath(a.right, a.y, px, py);
      rubber.setAttribute("d", d);
      rubber.classList.toggle("is-valid", !!valid);
    }
    if (scrolled) session.frame = requestAnimationFrame(() => dragFrame(session));
  };
  const scheduleFrame = session => { if (session.frame === null) session.frame = requestAnimationFrame(() => dragFrame(session)); };

  const beginDrag = (node, event) => {
    const session = { from: node, startX: event.clientX, startY: event.clientY, x: event.clientX, y: event.clientY, active: false,
      frame: null, dropEl: null, pointerId: event.pointerId };
    session.onMove = ev => {
      session.x = ev.clientX; session.y = ev.clientY;
      if (!session.active) {
        if (Math.hypot(ev.clientX - session.startX, ev.clientY - session.startY) < DRAG_THRESHOLD_PX) return;
        session.active = true;
        try { node.el.setPointerCapture?.(session.pointerId); } catch { /* kein Zeigerfang verfügbar */ }
        session.prevCursor = document.body.style.cursor;
        document.body.style.cursor = "grabbing";
        canvasRef.current?.classList.add("is-dragging");
        const rubber = rubberRef.current;
        if (rubber) { rubber.setAttribute("data-rubber", "true"); rubber.style.display = ""; }
        scrollRef.current?.addEventListener("scroll", session.onScroll);
        if (latest.current.picked) setPicked(null);
        highlight(node.key);
        dragFrame(session);
        return;
      }
      scheduleFrame(session);
    };
    // Gummiband beim Scrollen nachführen (Mausrad oder Rand-Auto-Scroll).
    session.onScroll = () => scheduleFrame(session);
    session.onUp = ev => {
      const { active, from } = session;
      // Treffer beim Loslassen neu bestimmen: zählt nur, was jetzt unter dem Zeiger liegt.
      const target = active ? nodeAtPoint(ev.clientX, ev.clientY) : null;
      stopDrag();
      if (!active) return;
      suppressClickRef.current = true;
      setTimeout(() => { suppressClickRef.current = false; }, 0);
      if (target && target.kind !== from.kind) latest.current.link(from, target);
    };
    session.onCancel = () => stopDrag();
    // Capture-Phase am Dokument: Escape bricht nur das Ziehen ab und erreicht den umgebenden Dialog nicht.
    session.onKey = ev => {
      if (ev.key !== "Escape") return;
      if (session.active) {
        ev.stopPropagation(); ev.preventDefault();
        setAnnouncement(latest.current.t("plantMapping.dragCancelled"));
      }
      stopDrag();
    };
    stopDrag();
    sessionRef.current = session;
    document.addEventListener("pointermove", session.onMove);
    document.addEventListener("pointerup", session.onUp);
    document.addEventListener("pointercancel", session.onCancel);
    document.addEventListener("keydown", session.onKey, true);
  };

  // --- Delegierte Bedienung der Zeichenfläche ---------------------------------------------------------------------
  const onCanvasPointerDown = event => {
    if (!latest.current.interactive || event.button !== 0) return;
    const node = nodeOf(event.target);
    if (!node) return;
    const pointerType = event.nativeEvent?.pointerType || event.pointerType;
    // Auf Touch und Stift zieht nur der Griff; der Rest des Knotens scrollt senkrecht (touch-action: pan-y).
    if (pointerType && pointerType !== "mouse" && !event.target.closest?.("[data-drag-handle]")) return;
    beginDrag(node, event);
  };
  const removeByHit = (hit, refocus) => {
    const { kennung, stage, id } = hit.dataset;
    latest.current.disconnect(kennung, stage, id, { refocus });
  };
  const onCanvasClick = event => {
    const hit = event.target.closest?.(".pim-hit");
    if (hit) { if (latest.current.interactive) removeByHit(hit, false); return; }
    const node = nodeOf(event.target);
    if (!node || !latest.current.interactive) return;
    if (suppressClickRef.current) { suppressClickRef.current = false; return; }
    const { picked: chosen, link: join, names: label, t: tr } = latest.current;
    const plain = { kind: node.kind, kennung: node.kennung, stage: node.stage, id: node.id, key: node.key };
    if (chosen?.key === plain.key) { setPicked(null); return; }
    if (chosen && join(chosen, plain)) { setPicked(null); return; }
    setPicked(plain);
    setAnnouncement(plain.kind === "plant"
      ? tr("plantMapping.pickedPlant", { werk: label(plain.kennung, null, DEFAULT_ID).werk })
      : tr("plantMapping.pickedInstance", { ziel: label(null, plain.stage, plain.id).ziel }));
  };
  const onCanvasKeyDown = event => {
    const hit = event.target.closest?.(".pim-hit");
    if (!hit || !latest.current.interactive) return;
    if (["Delete", "Backspace", "Enter", " ", "Spacebar"].includes(event.key)) { event.preventDefault(); removeByHit(hit, true); }
  };
  const onCanvasOver = event => {
    if (sessionRef.current?.active) return;
    const key = nodeOf(event.target)?.key || latest.current.picked?.key || null;
    if (key !== hotRef.current.key) highlight(key);
  };
  const onCanvasLeave = () => { if (!sessionRef.current?.active) highlight(latest.current.picked?.key || null); };
  const onRootKeyDown = event => {
    if (event.key !== "Escape") return;
    if (sessionRef.current?.active) { event.stopPropagation(); setAnnouncement(t("plantMapping.dragCancelled")); stopDrag(); return; }
    if (picked) { setPicked(null); event.stopPropagation(); }
  };

  // --- Speichern und Verwerfen ----------------------------------------------------------------------------
  const changedNames = changed.map(k => { const p = plantByKey.get(k); return p ? plantLabel(p) : k; }).join(", ");
  const save = async () => {
    setBusy(true); setMessage(null); setPicked(null);
    try {
      const res = await api("/api/cicd/plant-targets", { method: "PUT", body: JSON.stringify({ spaceId, revision: data.revision, entries: current }) });
      if (!mounted.current) return;
      if (res && typeof res.revision === "string" && res.entries && typeof res.entries === "object") {
        // Die PUT-Antwort trägt den gespeicherten Stand: sofort übernehmen, kein zweiter Abruf.
        setData(prev => ({ ...prev, ...res }));
        setDraft(res.entries);
        setMessage({ tone: "success", render: tr => tr("plantTargets.saved") });
      } else {
        const reloaded = await load({ keep: true });
        if (mounted.current) setMessage(reloaded.ok ? { tone: "success", render: tr => tr("plantTargets.saved") }
          : { tone: "critical", render: tr => tr("plantTargets.savedReloadFailed", { grund: loadReason(reloaded.info, tr) }) });
      }
    } catch (e) {
      if (!mounted.current) return;
      const grundRoh = e?.message || null;
      if (e?.code === "PLANT_TARGETS_BUSY") setMessage({ tone: "critical", render: tr => tr("plantTargets.busy") });
      else if (e?.code === "PLANT_TARGETS_CONFLICT" || (e?.status === 409 && !e?.code)) {
        const werke = changedNames || "-";
        setMessage({ tone: "critical", render: tr => tr("plantTargets.conflict", { werke }), conflict: true });
      } else if (e?.code === "PLANT_TARGETS_INVALID") {
        const d = e.details || {};
        const reason = d.reason === "target-unknown" && d.targetId ? "target-unknown-ziel" : d.reason;
        const variant = d.kennung && d.stage ? "invalid" : d.kennung ? "invalidWerk" : "invalidOhne";
        setMessage({ tone: "critical", render: tr => tr(`plantTargets.${variant}`, { werk: d.kennung || "", stage: d.stage ? stageLabel(d.stage) : "",
          grund: tr(`plantTargets.invalidReason.${reason}`, { ziel: d.targetId, defaultValue: tr("plantTargets.invalidReason.unknown") }) }) });
      } else if (e?.code === "STAGE_MULTI_TARGET_DISABLED") setMessage({ tone: "critical", render: tr => tr("plantTargets.disabled") });
      else setMessage({ tone: "critical", render: tr => tr("plantTargets.saveError", { grund: grundRoh || tr("plantTargets.grundUnbekannt") }) });
    }
    if (mounted.current) setBusy(false);
  };
  const discard = () => { setDraft(data?.entries || {}); setMessage(null); setPicked(null); };
  const reload = () => { setMessage(null); setPicked(null); load(); };
  const reloadAction = <Button size="small" disableRipple onClick={reload} sx={actionSx}>{t("plantTargets.reload")}</Button>;

  // --- Darstellung: MUI-Elemente mit Theme-Variablen; nur die Kanten sind eigenes SVG ---------------------------
  if (loadError) return <Box className="pim">
    {loadError.kind === "note"
      ? <Alert severity="info" role="status" sx={ALERT.info} action={reloadAction}>{t(loadError.key)}</Alert>
      : <Alert severity="error" role="alert" sx={ALERT.critical} action={reloadAction}>{t("plantTargets.loadError", { grund: loadReason(loadError, t) })}</Alert>}
  </Box>;
  if (!data) return <Box className="pim" role="status" aria-label={t("plantTargets.loading")} aria-busy="true">
    <Stack direction="row" spacing={2}>
      {[0, 1].map(col => <Stack key={col} spacing={1} sx={{ flex: 1 }}>
        <Skeleton variant="text" width="40%" animation={reducedMotion ? false : "pulse"} sx={skeletonSx}/>
        {[0, 1, 2].map(i => <Skeleton key={i} variant="rounded" height={52} animation={reducedMotion ? false : "pulse"} sx={skeletonSx}/>)}
      </Stack>)}
    </Stack>
  </Box>;
  if (mode === "single" || columns.length === 0) return <Box className="pim"><Alert severity="info" role="status" sx={ALERT.info}>{t("plantMapping.single")}</Alert></Box>;

  const pending = columns.flatMap(c => c.rows.filter(r => r.configured === false).map(r => `${colName(r)} (${stageLabel(c.key)})`));
  const staleText = item => {
    const werk = item.kennung;
    if (item.reason === "plant-removed") return t("plantTargets.stale.plantRemoved", { werk });
    if (item.reason === "stage-single") return t("plantTargets.stale.stageSingle", { werk, stage: stageLabel(item.stage) });
    return t("plantTargets.stale.targetRemoved", { werk, stage: stageLabel(item.stage), ziel: item.targetId || "" });
  };
  const anchor = key => {
    const n = geometry.nodes[key];
    if (!n) return { x: 0, y: 0 };
    return key.startsWith("plant:") ? { x: n.left, y: n.y } : { x: n.right, y: n.y };
  };
  const instanceAria = (row, stage) => [t("plantMapping.nodeInstance", { ziel: colName(row), stage: stageLabel(stage) }),
    row.isDefault && t("plantMapping.isDefault"), row.configured === false && t("plantMapping.notConfigured")].filter(Boolean).join(", ");

  // Fuß: Zähler und Ansage, Speichern und Verwerfen (im Dialogfuß über footer, sonst hier unten).
  const counterNode = editable && <Typography component="span" data-testid="pim-changes"
    sx={{ fontFamily: "inherit", fontSize: 13, fontWeight: dirty ? 600 : 400, color: dirty ? "var(--iio-text-1)" : "var(--iio-text-2)" }}>
    {dirty ? t("plantTargets.changes", { count: changed.length }) : staleCount ? t("plantTargets.staleWillGo") : t("plantTargets.changes", { count: 0 })}
  </Typography>;
  const statusNode = <Box sx={footer ? { display: "flex", flexDirection: "column", minWidth: 0 } : { display: "contents" }}>
    {footer && counterNode}
    <Typography role="status" aria-live="polite" sx={footer ? { ...statusSx, fontSize: 12, minHeight: 0 } : statusSx}>{announcement}</Typography>
  </Box>;
  const actionsNode = editable && <>
    <Button variant="outlined" disableRipple disabled={busy || !dirty} onClick={discard} sx={discardSx}>{t("plantTargets.discard")}</Button>
    <Button variant="contained" disableElevation disableRipple disabled={busy || (!dirty && !staleCount)} onClick={save} sx={saveSx}>
      {t(busy ? "plantTargets.saving" : "plantTargets.save")}
    </Button>
    {!footer && counterNode}
  </>;

  return (
    <Box className="pim" onKeyDown={onRootKeyDown} sx={rootSx}>
      <Typography sx={introSx}>{t("plantMapping.intro")}</Typography>
      {mode === "prepared" && <Alert severity="info" role="status" sx={ALERT.info}>
        <Box>{t("plantMapping.prepared")}</Box>
        {pending.length > 0 && <Box sx={{ mt: .5 }}>{t("plantMapping.preparedMissing", { namen: pending.join(", ") })}</Box>}
      </Alert>}
      {mode === "active" && ineffective.length > 0 && <Alert severity="warning" role="status" sx={ALERT.warning} data-testid="pim-ineffective">
        <Box sx={{ fontWeight: 600 }}>{t("plantMapping.ineffectiveTitle")}</Box>
        <Box component="ul" sx={{ m: 0, mt: .5, pl: 2.5 }}>{ineffective.map(item => {
          const label = names(item.kennung, item.stage, item.targetId);
          return <li key={`${item.kennung}|${item.stage}|${item.targetId}`}>{t("plantMapping.ineffectiveItem", label)}</li>;
        })}</Box>
        <Box sx={{ mt: .5 }}>{t("plantMapping.ineffectiveHint")}</Box>
      </Alert>}
      {sharingGaps.length > 0 && <Alert severity="warning" role="status" sx={ALERT.warning} data-testid="pim-sharing-gaps">
        {t("plantMapping.sharingGaps", { namen: sharingGaps.map(g => g.name || g.instanceId).join(", ") })}
      </Alert>}
      {!allowed && <Alert severity="info" role="status" sx={ALERT.info}>{t("plantMapping.readOnly")}</Alert>}
      {staleCount > 0 && <Alert severity="warning" role="status" sx={ALERT.warning}>
        <Box sx={{ fontWeight: 600 }}>{t("plantTargets.staleTitle")}</Box>
        <Box component="ul" sx={{ m: 0, mt: .5, pl: 2.5 }}>{data.stale.map((item, i) => <li key={i}>{staleText(item)}</li>)}</Box>
      </Alert>}

      <Box sx={toolsSx}>
        <ToggleButtonGroup size="small" exclusive value={view} aria-label={t("plantMapping.viewLabel")} sx={toggleSx}
          onChange={(_, next) => { if (next) { stopDrag(); setView(next); setPicked(null); } }}>
          <ToggleButton value="graph" disableRipple>{t("plantMapping.viewGraph")}</ToggleButton>
          <ToggleButton value="table" disableRipple>{t("plantMapping.viewTable")}</ToggleButton>
        </ToggleButtonGroup>
        {view === "graph" && <>
          <TextField size="small" type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder={t("plantTargets.search")}
            inputProps={{ "aria-label": t("plantTargets.search") }} sx={graphSearchSx}/>
          {columns.length > 1 && <TextField select size="small" label={t("plantMapping.stageFilter")} value={stageFilter} onChange={e => setStageFilter(e.target.value)}
            id="pim-stage-filter" SelectProps={{ native: true }} sx={filterSx}>
            <option value="all">{t("plantMapping.stageAll")}</option>
            {columns.map(c => <option key={c.key} value={c.key}>{stageLabel(c.key)}</option>)}
          </TextField>}
        </>}
      </Box>

      {view === "table" ? (
        <CicdPlantTargetCatalog columns={columns} baseStage={baseStage} plants={plants} entries={current} original={original}
          onChange={next => { setDraft(next); setMessage(null); }} t={t} disabled={busy} readOnly={!editable}/>
      ) : (
        <>
          <Box aria-hidden="true" sx={legendSx}>
            <Box component="span" sx={legendItemSx}><svg width="28" height="8"><path d="M1,4 H27" className="pim-wire is-own"/></svg>{t("plantMapping.legendOwn")}</Box>
            <Box component="span" sx={legendItemSx}><svg width="28" height="8"><path d="M1,4 H27" className="pim-wire is-default"/></svg>{t("plantMapping.legendDefault")}</Box>
            {unconfigured.size > 0 && <Box component="span" sx={legendItemSx}><svg width="28" height="8"><path d="M1,4 H27" className="pim-wire is-own is-ineffective"/></svg>{t("plantMapping.legendIneffective")}</Box>}
          </Box>
          {plants.length === 0 && <Alert severity="info" role="status" sx={ALERT.info}>{t("plantMapping.keineWerke")}</Alert>}
          {/* Eine Scrollfläche für beide Spalten: Kanten liegen im Koordinatensystem der Zeichenfläche. */}
          <Box ref={scrollRef} sx={scrollSx}>
            <Box ref={canvasRef} className="pim-canvas" sx={canvasSx} aria-busy={busy || undefined}
              onPointerDown={onCanvasPointerDown} onClick={onCanvasClick} onKeyDown={onCanvasKeyDown}
              onMouseOver={onCanvasOver} onFocus={onCanvasOver} onMouseLeave={onCanvasLeave} onBlur={onCanvasLeave}>
              <Stack component="section" spacing={1.5} data-map-column="instances" aria-label={t("plantMapping.colInstances")} sx={columnSx}>
                <Typography component="h3" aria-hidden="true" sx={colTitleSx}>{t("plantMapping.colInstances")}</Typography>
                {visibleColumns.map(stage => <Stack key={stage.key} spacing={1}>
                  <Typography component="h4" sx={stageHeadSx}>{stageLabel(stage.key)}</Typography>
                  <Stack component="ul" spacing={1} sx={listSx}>
                    {stage.rows.map(row => {
                      const id = idOf(row);
                      const key = instanceKeyOf(stage.key, id);
                      return <li key={key}>
                        <MapNode cardId={key} kind="instance" stage={stage.key} targetId={id} title={colName(row)}
                          meta={[row.region, row.environment].filter(Boolean).join(" · ") || null} isDefault={row.isDefault} pending={row.configured === false}
                          count={counts[key] || 0} picked={picked?.key === key} editable={editable} disabled={busy} ariaLabel={instanceAria(row, stage.key)} t={t}/>
                      </li>;
                    })}
                  </Stack>
                </Stack>)}
              </Stack>
              <Box aria-hidden="true"/>
              <Stack component="section" spacing={1.5} data-map-column="plants" aria-label={t("plantMapping.colPlants")} sx={columnSx}>
                <Typography component="h3" aria-hidden="true" sx={colTitleSx}>{t("plantMapping.colPlants")}</Typography>
                {visiblePlants.length === 0 && plants.length > 0 && <Typography sx={mutedSx}>{t("plantTargets.keinTreffer")}</Typography>}
                <Stack component="ul" spacing={1} sx={listSx}>
                  {visiblePlants.map(plant => {
                    const key = plantKeyOf(plant.kennung);
                    return <li key={key}>
                      <MapNode cardId={key} kind="plant" kennung={plant.kennung} title={plant.name || plant.kennung} meta={plant.name ? plant.kennung : null}
                        count={null} picked={picked?.key === key} editable={editable} disabled={busy}
                        ariaLabel={t("plantMapping.nodePlant", { werk: plantLabel(plant) })} t={t}/>
                    </li>;
                  })}
                </Stack>
              </Stack>
              <svg ref={svgRef} className="pim-wires" width={geometry.width || undefined} height={geometry.height || undefined}
                viewBox={`0 0 ${geometry.width || 1} ${geometry.height || 1}`} preserveAspectRatio="none" role="group" aria-label={t("plantMapping.edgesLabel")}>
                {edges.map(edge => {
                  const a = instanceKeyOf(edge.stage, edge.id), b = plantKeyOf(edge.kennung);
                  const from = anchor(a), to = anchor(b);
                  const d = wirePath(from.x, from.y, to.x, to.y);
                  const id = `${edge.kennung}|${edge.stage}|${edge.id}`;
                  const cls = "pim-wire is-" + edge.kind + (edge.ineffective ? " is-ineffective" : "");
                  // Nicht entfernbar: Standardkanten, das Standardziel der Basisstufe, nur Lesen und während des Speicherns.
                  if (edge.locked || !interactive) return <path key={id} d={d} className={cls} data-edge={id} data-edge-kind={edge.kind} data-ineffective={edge.ineffective || undefined} data-a={a} data-b={b} aria-hidden="true"/>;
                  const label = t("plantMapping.edgeRemove", names(edge.kennung, edge.stage, edge.id)) + (edge.ineffective ? `, ${t("plantMapping.edgeIneffective")}` : "");
                  // Trefferfläche zuerst, damit `.pim-hit:hover + .pim-wire` die sichtbare Kante einfärbt.
                  return <g key={id}>
                    <path d={d} className="pim-hit" role="button" tabIndex={0} aria-label={label} data-kennung={edge.kennung} data-stage={edge.stage} data-id={edge.id}>
                      <title>{label}</title>
                    </path>
                    <path d={d} className={cls} data-edge={id} data-edge-kind="own" data-ineffective={edge.ineffective || undefined} data-a={a} data-b={b} aria-hidden="true"/>
                  </g>;
                })}
                <path ref={rubberRef} className="pim-wire is-rubber" aria-hidden="true" style={{ display: "none" }}/>
              </svg>
            </Box>
          </Box>
        </>
      )}

      {!footer && statusNode}
      {!footer && actionsNode && <Box sx={footSx}>{actionsNode}</Box>}
      {footer?.status && createPortal(statusNode, footer.status)}
      {footer?.actions && actionsNode && createPortal(actionsNode, footer.actions)}
      {message && <Alert severity={message.tone === "success" ? "success" : "error"} role={message.tone === "success" ? "status" : "alert"} sx={ALERT[message.tone]}
        action={message.conflict ? reloadAction : undefined}>
        {message.render(t)}
      </Alert>}
    </Box>
  );
}
